import { Stack } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickersLocales from "@mui/x-date-pickers/locales";
import { useSnackbar } from "notistack";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isExpired } from "react-jwt";
import { Outlet, useLocation } from "react-router-dom";
import cookie from "react-cookies";
import service from "../api";
import { UAM_PREFIX } from "../api/admin";
import { EXPORT_AUDIT_LOG_URL } from "../api/audit";
import { SYSLIB_PREFIX } from "../api/systemLibrary";
import eventBus from "../common/eventBus";
import { getURLDomain, logoutFunction } from "../common/utils";
import Loading from "../components/Loading";
import { LANGUAGEMAP, RIMS_COOKIE_STG_CONSTANTS, RTQ_EVENTS } from "../constants";
import { useConfigStore } from "../store/config";
import styles from "./index.module.scss";
import { FreyrLibraryProvider } from "@freyr/freyr-react-common";
import { useCookieData } from "../hooks/useCookieData";
type SupportedLocales = keyof typeof coreLocales;
type GridLocales = keyof typeof gridLocales;
type PickersLocales = keyof typeof pickersLocales;

const Layouts = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [auth, setAuth] = useState<boolean>(false);
  const [language] = useConfigStore((state) => [state.language]);
  const theme = useTheme();
  const [authList, setAuthList] = useState<any>([]);
  const [loadingWord, setLoadingWord] = useState('');
  const URLDomain = getURLDomain();
  const redirectURL = `https://portal.${URLDomain}`;
  const { pathname } = useLocation();
  const location = useLocation();
  const { token, tenantId } = useCookieData();

  const themeWithLocale = useMemo(() => {
    const locale: SupportedLocales = LANGUAGEMAP.mui[language];
    const gridLocale: GridLocales = LANGUAGEMAP.mui[language];
    const pickersLocale: PickersLocales = LANGUAGEMAP.mui[language];
    return createTheme(
      {
        palette: {},
      },
      coreLocales[locale], //core translations
      gridLocales[gridLocale], // x-data-grid translations
      pickersLocales[pickersLocale] // x-date-pickers translations
    );
  }, [language, theme]);

  useEffect(() => {
    i18n.changeLanguage(language)
  }, [language])


  const downloadFileCommon = (url: string, data = {}, enqueueSnackbar: any, type = '') => {
    const dataLength = Object.keys(data).length
    let executePolling = setTimeout(
      () => {
        service({
          method: dataLength ? 'post' : 'get',
          url: url,
          responseType: 'arraybuffer',
          data: data
        }).then((res: any) => {
          if (res.headers['export-status'] == 3 || type === 'download template') {
            eventBus.emit(RTQ_EVENTS.COMMON.EXPORT_FILE_END)
            eventBus.emit(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END) // download template file
            clearTimeout(executePolling);
            let fileName = res.headers['content-disposition'].split(';')[1].split('filename=')[1];
            if (!res.data) { return }
            let url = window.URL.createObjectURL(new Blob([res.data]));
            let element = document.createElement('a');
            element.style.display = 'none';
            element.href = url;
            element.setAttribute('download', decodeURIComponent(fileName));
            document.body.appendChild(element);
            element.click();
          } else {
            downloadFileCommon(url, data, enqueueSnackbar);
          }
        }).catch((err: any) => {
          clearTimeout(executePolling);
          eventBus.emit(RTQ_EVENTS.COMMON.EXPORT_FILE_END)
          eventBus.emit(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END) // download template file
          if (err?.response?.status == 401 || err?.response?.data?.message === "unauthorized") {
            return;
          };
          if (enqueueSnackbar) {
            const unit8Msg: any = new Uint8Array(err.response.data)
            const error_msg = JSON.parse(String.fromCharCode.apply(null, unit8Msg))
            enqueueSnackbar(error_msg.message, { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            // enqueueSnackbar('Failed to download the PDF, Please try again later', { variant: "error", anchorOrigin: { vertical: 'top', horizontal: 'center' } });
          }
        })
      }, 2000)

  }

  const readyToPolling = (api: string, data: any, enqueueSnackbar: any) => {
    let timeZone = -new Date().getTimezoneOffset() / 60
    // if (api.indexOf(`${UAM_PREFIX}`) > -1 || api.indexOf(`${SYSLIB_PREFIX}`) > -1) {
    //   downloadFileCommon(`${api}&timeZone=${timeZone}`, {}, enqueueSnackbar)
    // } else {
    const downloadData = {
      ...data,
      "timeZone": timeZone
    }
    downloadFileCommon(api, downloadData, enqueueSnackbar)
    // }
  }

  useEffect(() => {
    let blLogin = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
    // let blLogin = 'eyJraWQiOiIzWDJlM1JaSGpMQU9CZXhLRTMzRkhNcGJscVdFZFp4ZCtqRXArSXpUNFc4PSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiJjOWUzMTU1NC0yZmNlLTQ1NzItOGNmMC1hZDM1YjdjMWVmMTMiLCJjb2duaXRvOmdyb3VwcyI6WyJ0ZW5hbnRfMCJdLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb21cL3VzLWVhc3QtMV9Senh2RWFwOXQiLCJjbGllbnRfaWQiOiIxa2U5aDVmYmlhbG1ndXFodXFkb3Bqa2M4dCIsIm9yaWdpbl9qdGkiOiI0M2ViNDE5Mi1mMGM3LTQ0ZDktYWNhNS01ZmI3ODExZGVlZWQiLCJldmVudF9pZCI6IjUzOTZjNmI2LTU0MzAtNDA4Mi1iYzFiLTcyZWJhYTlmNzRhZCIsInRva2VuX3VzZSI6ImFjY2VzcyIsInNjb3BlIjoiYXdzLmNvZ25pdG8uc2lnbmluLnVzZXIuYWRtaW4iLCJhdXRoX3RpbWUiOjE3MTU4MjgyNjQsImV4cCI6MTcxNTkxNDY2NCwiaWF0IjoxNzE1ODI4MjY0LCJqdGkiOiIxNDU4ODIxMi1iNmQzLTQ5NzctYTVhNy04MDIxYWQzMTYzMDEiLCJ1c2VybmFtZSI6InN1cGVycm9vdCJ9.MKAl5hZlhUZIH_deWAYk-4M1ORGFQ9lc6zLNrzZjHog-oIjM9fVYrsHul1slxy4lMAm7mjhcAsN7wWmhFB40C7J_z5O7VWkFGtWV1zE1FgeXZkh0rkEuHmWHnNuor0kQKNOo8WUOzvdXrJkikVJuX9dvHVw9F54A8yBGsuoyc3r3DB-N7pC8ggkiTWG3ou8knQYx8x-ReFNMmmUz4Ly8bDEWPdZLQ07R_y9oW9r-dR455Id0RIVk1Be6L4HbMRP7el0GjFuiy4Rkf4lzBE9S0VYGb4zoyL3UMyVEtnZNeK-yLSe4zNJvP2El9karmmHVFEJWR5acIEUwshe6j_Mlcw'
    if (blLogin === null || isExpired(blLogin)) {
      // setAuth(true);
      logoutFunction();
      window.location.href = `${redirectURL}?redirect=${window.location.origin}`;
    } else {
      setAuth(true)
    }




    eventBus.addListener(RTQ_EVENTS.COMMON.EXPORT_FILE_START, exportFileStart)
    eventBus.addListener(RTQ_EVENTS.COMMON.EXPORT_FILE_END, exportFileEnd)

    eventBus.addListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_START, downloadTemStart)
    eventBus.addListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END, downloadTemEnd)
    return () => {
      eventBus.removeListener(RTQ_EVENTS.COMMON.EXPORT_FILE_START, exportFileStart)
      eventBus.removeListener(RTQ_EVENTS.COMMON.EXPORT_FILE_END, exportFileEnd)

      eventBus.removeListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_START, downloadTemStart)
      eventBus.removeListener(RTQ_EVENTS.ADMIN.DOWNLOAD_TEMPLATE_END, downloadTemEnd)
    }
  }, []);

  const exportFileStart = (func: any) => {
    if (func.api === EXPORT_AUDIT_LOG_URL) {
      setLoadingWord('We are currently exporting your data. Please check your downloads once its completed. Please see, only the first 10,000 records can be exported.')
    } else {
      setLoadingWord('We are currently exporting your data. Please check your downloads once its completed.')
    }
    setLoading(true);
    if (func.api) {
      readyToPolling(func.api, func.data, func.enqueueSnackbar)
    } else {
      func();
    }
  }

  const exportFileEnd = (func: any) => {
    setLoading(false);
    setLoadingWord('')
  }

  const downloadTemStart = (func: any) => {
    setLoadingWord('We are currently exporting your data. Please check your downloads once its completed.')
    setLoading(true);
    if (func.api) {
      let timeZone = -new Date().getTimezoneOffset() / 60
      if (func.api.indexOf(`${UAM_PREFIX}`) > 0 || func.api.indexOf(`${SYSLIB_PREFIX}`) > 0) {
        downloadFileCommon(func.api, func.data, func.enqueueSnackbar, 'download template')
      } else {
        const downloadData = {
          ...func.data,
          "timeZone": timeZone
        }
        downloadFileCommon(func.api, downloadData, func.enqueueSnackbar, 'download template')
      }
    } else {
      func();
    }
  }

  const downloadTemEnd = (func: any) => {
    setLoading(false);
  }


  return (
    <>
      {auth && (
        <ThemeProvider theme={themeWithLocale}>
          <FreyrLibraryProvider
            token={token}
            language={language}
            tenant={tenantId}>
            <div className={styles["layout"]}>
              <Stack flexDirection="row" sx={{ flex: 1, overflowY: "auto" }}>
                <section className="layout-right">
                  <div
                    className="page-wrapper"
                    style={{ position: "relative" }}
                  >
                    <div className="customer-page">
                      <Suspense>
                        <Outlet />
                      </Suspense>
                    </div>
                  </div>
                </section>
              </Stack>
            </div>
            <Loading open={loading} wording={loadingWord} />
          </FreyrLibraryProvider>
        </ThemeProvider>
      )}
    </>
  );
};

export default Layouts;
